import React from "react";

import PropTypes from "prop-types";

//---------------------------------------------------------------------------
// MUI Icons
//---------------------------------------------------------------------------
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import Close from "@mui/icons-material/Close";
import ErrorOutlined from "@mui/icons-material/ErrorOutlined";
import Info from "@mui/icons-material/Info";
import WarningAmber from "@mui/icons-material/WarningAmber";

//---------------------------------------------------------------------------
// MUI Components
//---------------------------------------------------------------------------
import ReactAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import { useTheme } from "@mui/material/styles";

import DialogTitleBar from "./DialogTitleBar.jsx";

const LEVEL_CONFIG = {
  error: { Icon: ErrorOutlined, color: "error", defaultTitle: "Error" },
  warning: { Icon: WarningAmber, color: "warning", defaultTitle: "Warning" },
  info: { Icon: Info, color: "info", defaultTitle: "Info" },
  success: { Icon: CheckCircleOutlined, color: "success", defaultTitle: "Success" },
};

function Alert({
  // Props
  title,
  message,
  setMessage,
  level,
  variant,
  "data-cy": dataCy,
}) {
  const handleClose = React.useCallback(() => setMessage(null), [setMessage]);
  const theme = useTheme();

  if (variant === "dialog") {
    const { Icon, color, defaultTitle } = LEVEL_CONFIG[level];
    const dialogTitleText = title || defaultTitle;
    return (
      <Dialog open={!!message} maxWidth="sm" data-cy={dataCy} fullWidth>
        <DialogTitleBar title={dialogTitleText} Icon={Icon} iconColor={color} />
        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        {setMessage && (
          <DialogActions>
            <Button variant="contained" color="secondary" onClick={handleClose} data-cy="close-alert">
              Okay
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
  if (variant === "snackbar") {
    return (
      <Snackbar
        open={!!message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: { xs: 90, sm: 0 }, maxWidth: "lg" }}
        data-cy={dataCy}
      >
        <ReactAlert
          severity={level}
          variant="outlined"
          action={
            setMessage && (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleClose}
                data-cy="close-alert"
              >
                <Close fontSize="inherit" />
              </IconButton>
            )
          }
          sx={{
            mb: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {message}
        </ReactAlert>
      </Snackbar>
    );
  }
  return (
    <Box sx={{ width: "100%" }}>
      <Collapse in={!!message}>
        <ReactAlert
          severity={level}
          data-cy={dataCy}
          action={
            setMessage && (
              <IconButton
                aria-label="close"
                color={level === "info" ? "secondary" : "inherit"}
                size="small"
                onClick={handleClose}
                data-cy="close-alert"
              >
                <Close fontSize="inherit" />
              </IconButton>
            )
          }
          sx={{ ...(level === "info" && setMessage && { backgroundColor: "white" }) }}
        >
          {message}
        </ReactAlert>
      </Collapse>
    </Box>
  );
}

Alert.propTypes = {
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  setMessage: PropTypes.func,
  level: PropTypes.string.isRequired,
  variant: PropTypes.string,
  "data-cy": PropTypes.string,
};

export default Alert;
